import { Event } from '@/lib/types';
import { endOfDay, format, startOfDay } from 'date-fns';
import {
  getEventsForDay,
  isEventInDateRange,
  isMultiDayEvent,
} from '@/utils/eventUtils';
import {
  createDaysWithEventsMap,
  processMultiDayEvents,
} from '@/utils/calendarEventLayout';

export type CalendarSlot =
  | { type: 'day'; day: Date; originalIndex: number; isEmpty: boolean; spanDays: 1 }
  | { type: 'gap'; days: Date[]; firstIndex: number; spanDays: number };

export function buildCalendarSlots(
  days: Date[],
  filteredEvents: Event[],
  threshold = 2,
): CalendarSlot[] {
  if (days.length === 0) return [];

  const startDate = startOfDay(days[0]);
  const endDate = endOfDay(days[days.length - 1]);
  const visibleEvents = filteredEvents.filter((event) =>
    isEventInDateRange(event, startDate, endDate),
  );
  const multiDayEvents = visibleEvents.filter(isMultiDayEvent);
  const processedMultiDay = processMultiDayEvents(multiDayEvents, days);
  const daysWithMultiDay = createDaysWithEventsMap(processedMultiDay, days);

  const isDayEmpty = (day: Date) => {
    const key = format(day, 'yyyy-MM-dd');
    if (daysWithMultiDay.get(key)) return false;
    const single = getEventsForDay(visibleEvents, day).filter(
      (event) => !isMultiDayEvent(event),
    );
    return single.length === 0;
  };

  const slots: CalendarSlot[] = [];
  let gap: { day: Date; originalIndex: number }[] = [];

  const flushGap = () => {
    if (gap.length === 0) return;
    if (gap.length >= threshold) {
      slots.push({
        type: 'gap',
        days: gap.map((g) => g.day),
        firstIndex: gap[0].originalIndex,
        spanDays: gap.length,
      });
    } else {
      gap.forEach((g) =>
        slots.push({
          type: 'day',
          day: g.day,
          originalIndex: g.originalIndex,
          isEmpty: true,
          spanDays: 1,
        }),
      );
    }
    gap = [];
  };

  days.forEach((day, originalIndex) => {
    if (isDayEmpty(day)) {
      gap.push({ day, originalIndex });
    } else {
      flushGap();
      slots.push({
        type: 'day',
        day,
        originalIndex,
        isEmpty: false,
        spanDays: 1,
      });
    }
  });
  flushGap();

  return slots;
}
